import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useResetRecoilState } from 'recoil';
import useSWR from 'swr';

import { Spinner } from 'components';

import { API_ROUTES } from 'lib/api-routes';
import { signUpRecruiterState } from 'lib/atoms/signUpRecruiterAtom';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { withAuth } from 'lib/hocs/withAuth';
import { CompanyAttributes } from 'lib/models/company';
import { WorkingModel } from 'lib/models/job';
import { TouchpointState } from 'lib/models/opportunity';
import { TagAttributes, TagType } from 'lib/models/tag';
import {
  TouchpointAttributes,
  TouchpointsResponse,
  TouchpointType,
} from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { fetchTranslationFiles, LOCALE_PAGES } from 'lib/services/page';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import styles from './Onboarding.module.scss';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import { TouchpointCartDataProps } from './TouchpointCard';
import Welcome from './Welcome';

export interface Touchpointable {
  end_date: string | null;
  full_time: boolean | null;
  id: string;
  maximum_salary: number | null;
  minimum_salary: number | null;
  start_date: string | null;
  working_model: WorkingModel | null;
}

const Onboarding = (): ReactElement => {
  const router = useRouter();
  const { isLoggedIn, isRecruiter, onUserLogout, recruiter } = useAuth();
  const resetState = useResetRecoilState(signUpRecruiterState);

  const [flowState, setFlowState] = useState<string>(
    recruiter?.flow_state || ONBOARDING_FLOW_STATE.PRE_COMPLETE
  );

  useEffect(() => {
    if (recruiter?.flow_state) {
      setFlowState(recruiter.flow_state);
    }
  }, [recruiter]);

  const { approved } = router.query;

  if (approved) {
    resetState();
  }

  useEffect(() => {
    if (isLoggedIn && !isRecruiter) {
      onUserLogout();
      router.replace(PAGE_ROUTES.SIGN_IN_RECRUITER);
    }
  }, [isLoggedIn, isRecruiter]);

  const { data: touchpointsByHuzzleResponse, isLoading } =
    useSWR<TouchpointsResponse>(
      `${API_ROUTES.COMPANY_TOUCHPOINTS}?with_details=true&apply_url=true&touchpointable_types[]=${TouchpointType.Job}&touchpointable_types[]=${TouchpointType.Internship}&per_page=10`,
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

  const campaignsByHuzzle = useMemo(() => {
    const campaigns = parseArrayResponse<TouchpointAttributes>(
      touchpointsByHuzzleResponse
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formattedCampaigns = campaigns.map((campaign: any) => {
      const touchpointable = parseResponse<Touchpointable>(
        campaign.touchpointable
      );
      const creatableFor = parseResponse<CompanyAttributes>(
        campaign.creatable_for
      );

      const area_of_responsibilities = parseArrayResponse<
        TagAttributes<TagType.AreaOfResponsibility>
      >(campaign.area_of_responsibilities)
        .map((item: { name: string }) => item.name)
        ?.join(', ');

      return {
        id: campaign.id,
        title: campaign.title,
        application_deadline: campaign.application_deadline,
        maximum_salary: touchpointable.maximum_salary,
        minimum_salary: touchpointable.minimum_salary,
        pay_currency: campaign.pay_currency,
        touchpointable_type: campaign.touchpointable_type,
        isExpired: campaign.state === TouchpointState.EXPIRED,
        url: `${window.location.origin}${getOpportunityPublicPagePathname(
          campaign.touchpointable_type,
          campaign.slug
        )}`,
        kind: campaign.kind,
        working_model: campaign.working_model,
        area_of_responsibilities: area_of_responsibilities,
        created_at: campaign.created_at,
        company: {
          name: creatableFor.name,
          logo: creatableFor.icon_url,
          is_verified: creatableFor.is_verified,
          showLogo: false,
        },
      } as TouchpointCartDataProps;
    });

    return formattedCampaigns;
  }, [touchpointsByHuzzleResponse]);

  const stepStr = flowState?.match(/\d+/g);

  const step = stepStr ? parseInt(stepStr[0]) + 1 : 1;

  const progress = (step / 6) * 100;

  useEffect(() => {
    if (flowState === ONBOARDING_FLOW_STATE.COMPLETE) {
      router.replace(PAGE_ROUTES.RECRUITER_DASHBOARD);
    }
  }, [flowState]);

  if (isLoading)
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );

  return (
    <>
      <Head>
        <title>Onboarding | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.Onboarding} data-testid="onboarding">
        {flowState === ONBOARDING_FLOW_STATE.PRE_COMPLETE && (
          <Welcome
            touchpointsExist={!!campaignsByHuzzle.length}
            setFlowState={setFlowState}
          />
        )}

        {/* company touchpoint with apply url */}
        {flowState === ONBOARDING_FLOW_STATE.POST_1 && (
          <Step1
            progress={progress}
            touchpoint={campaignsByHuzzle}
            setFlowState={setFlowState}
          />
        )}

        {/* roles screen */}
        {flowState === ONBOARDING_FLOW_STATE.POST_2 && (
          <Step2 progress={progress} setFlowState={setFlowState} />
        )}

        {/* invite colleagues  */}
        {flowState === ONBOARDING_FLOW_STATE.POST_3 && (
          <Step3
            progress={progress}
            touchpointsExist={!!campaignsByHuzzle.length}
            setFlowState={setFlowState}
          />
        )}

        {/* complete company profile */}
        {flowState === ONBOARDING_FLOW_STATE.POST_4 && (
          <Step4 progress={progress} setFlowState={setFlowState} />
        )}

        {/* add opportunities  */}
        {flowState === ONBOARDING_FLOW_STATE.POST_5 && (
          <Step5 progress={progress} setFlowState={setFlowState} />
        )}
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.ONBOARDING_RECRUITER],
      })),
    },
  };
}

const DashboardWrapper = withAuth(Onboarding);

export default DashboardWrapper;
